/**
 * Настройки → Уведомления (docs/06-notifications.md, docs/08-screens.md).
 *
 * Каждый тип включается отдельно и на уровне пользователя: отключённый
 * тип не создаёт даже записи в панели. Панель отключить нельзя — она архив,
 * поэтому её переключатель показан, но недоступен.
 */
import { useEffect, useState, type CSSProperties } from 'react';
import { Badge, Button, Card, Checkbox, Icon, Switch } from '@/ds';
import { Provenance } from '@/components/money';
import {
  useNotificationSettings,
  useOperation,
  type NotificationChannel,
  type NotificationTypeSetting,
} from '@/api/hooks';
import { formatDateTime, formatRelative } from '@/lib/format';
import { useControlSize } from '@/lib/useControlSize';
import { currentEndpoint, pushPermission, subscribeToPush, unsubscribeFromPush } from '@/lib/push';
import { PlacesCard, TaskDigestCard } from './TelegramPlaces';
import { QueryState } from '@/components/QueryState';

const CHANNELS: Array<{ value: NotificationChannel; label: string }> = [
  { value: 'in_app', label: 'Панель' },
  { value: 'telegram', label: 'Telegram' },
  { value: 'web_push', label: 'Браузер' },
];

const SECTION_TITLE: CSSProperties = {
  font: '500 16px var(--font-display)',
  letterSpacing: 'var(--ls-heading)',
  color: 'var(--text-strong)',
};

function TypeRow({
  setting,
  onChange,
  disabled,
}: {
  setting: NotificationTypeSetting;
  onChange: (input: { type: string; isEnabled?: boolean; channels?: NotificationChannel[] }) => void;
  disabled: boolean;
}) {
  const toggleChannel = (channel: NotificationChannel, on: boolean) => {
    const next = on
      ? [...new Set([...setting.channels, channel])]
      : setting.channels.filter((item) => item !== channel);
    onChange({ type: setting.type, channels: next });
  };

  return (
    <div
      style={{
        display: 'flex',
        gap: 'var(--space-4)',
        flexWrap: 'wrap',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        paddingBottom: 'var(--space-4)',
        borderBottom: '1px solid var(--border-subtle)',
      }}
    >
      <div style={{ flex: '1 1 280px', minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)',
            flexWrap: 'wrap',
          }}
        >
          <span style={{ font: '500 14px var(--font-text)', color: 'var(--text-strong)' }}>
            {setting.title}
          </span>
          {setting.level === 'critical' ? <Badge tone="danger">срочно</Badge> : null}
          {setting.personal ? <Badge tone="neutral">только ответственному</Badge> : null}
        </div>
        <Provenance>{setting.description}</Provenance>
      </div>

      <div
        style={{
          display: 'flex',
          gap: 'var(--space-5)',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        {CHANNELS.map((channel) => (
          <Checkbox
            key={channel.value}
            label={channel.label}
            checked={setting.channels.includes(channel.value)}
            disabled={disabled || !setting.isEnabled || channel.value === 'in_app'}
            onChange={(e) => toggleChannel(channel.value, e.target.checked)}
          />
        ))}
        <Switch
          checked={setting.isEnabled}
          disabled={disabled}
          onChange={(e) => onChange({ type: setting.type, isEnabled: e.target.checked })}
        />
      </div>
    </div>
  );
}

function TelegramCard() {
  const size = useControlSize();
  const settings = useNotificationSettings(true);
  const request = useOperation<Record<string, never>, { code: string; expiresAt: string }>(
    'telegram.request_code',
  );
  const unlink = useOperation<Record<string, never>, { linked: boolean }>('telegram.unlink');

  const telegram = settings.data?.telegram;
  if (!telegram) return null;

  const link = telegram.botUsername ? `https://t.me/${telegram.botUsername}` : null;

  return (
    <Card padding="md">
      <div style={{ ...SECTION_TITLE, marginBottom: 'var(--space-4)' }}>Telegram</div>

      {!telegram.configured ? (
        <Provenance>
          Токен бота не задан в окружении сервера. Пока его нет, Telegram-уведомления никому
          не отправляются.
        </Provenance>
      ) : telegram.linked ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
            <Badge tone="success" dot>привязан</Badge>
            <span style={{ font: '400 14px var(--font-text)', color: 'var(--text-muted)' }}>
              {telegram.username ? `@${telegram.username}` : 'учётная запись без имени'}
            </span>
          </div>
          {telegram.linkedAt ? (
            <Provenance>{`Привязано ${formatDateTime(telegram.linkedAt)}`}</Provenance>
          ) : null}
          <div>
            <Button
              variant="secondary"
              size={size}
              loading={unlink.isPending}
              onClick={() => unlink.mutate({} as Record<string, never>)}
            >
              Отвязать
            </Button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <p style={{ font: '400 14px/1.6 var(--font-text)', color: 'var(--text-muted)', maxWidth: 560 }}>
            Уведомления приходят в личный чат с ботом. Получите код, откройте бота и отправьте
            код сообщением. Без привязки Telegram-уведомления просто не отправляются.
          </p>

          {telegram.pendingCode ?? request.data?.code ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              <div
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 26,
                  letterSpacing: '0.12em',
                  color: 'var(--text-strong)',
                }}
              >
                {telegram.pendingCode ?? request.data?.code}
              </div>
              <Provenance>
                {telegram.pendingCodeExpiresAt
                  ? `Код действует до ${formatDateTime(telegram.pendingCodeExpiresAt)}. Он одноразовый.`
                  : 'Код одноразовый и живёт пятнадцать минут.'}
              </Provenance>
            </div>
          ) : null}

          <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
            <Button
              variant="secondary"
              size={size}
              loading={request.isPending}
              onClick={() => request.mutate({} as Record<string, never>)}
            >
              {telegram.pendingCode ? 'Получить новый код' : 'Получить код'}
            </Button>
            {link ? (
              <Button
                variant="ghost"
                size={size}
                as="a"
                href={link}
                iconRight={<Icon name="ExternalLink" size={15} />}
              >
                Открыть бота
              </Button>
            ) : null}
          </div>
        </div>
      )}
    </Card>
  );
}

function WebPushCard() {
  const size = useControlSize();
  const settings = useNotificationSettings(true);
  const subscribe = useOperation<
    { endpoint: string; p256dh: string; auth: string; userAgent: string },
    { id: string }
  >('push.subscribe');
  const unsubscribe = useOperation<{ endpoint: string }, { count: number }>('push.unsubscribe');

  const [error, setError] = useState<string | null>(null);
  const [thisDevice, setThisDevice] = useState<string | null>(null);

  useEffect(() => {
    void currentEndpoint().then(setThisDevice);
  }, [settings.data]);

  const webPush = settings.data?.webPush;
  if (!webPush) return null;

  const permission = pushPermission();
  const devices = webPush.devices;
  // Адреса подписок наружу не отдаются: этот браузер узнаёт себя сам.
  const linkedHere = thisDevice !== null;

  const enable = async () => {
    setError(null);
    if (!webPush.publicKey) {
      setError('Ключи web push не заданы на сервере');
      return;
    }
    try {
      const payload = await subscribeToPush(webPush.publicKey);
      await subscribe.mutateAsync(payload);
      setThisDevice(payload.endpoint);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось включить уведомления');
    }
  };

  const disable = async () => {
    setError(null);
    try {
      const endpoint = await unsubscribeFromPush();
      if (endpoint) await unsubscribe.mutateAsync({ endpoint });
      setThisDevice(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось отключить уведомления');
    }
  };

  return (
    <Card padding="md">
      <div style={{ ...SECTION_TITLE, marginBottom: 'var(--space-4)' }}>Уведомления в браузере</div>

      {!webPush.configured ? (
        <Provenance>
          Ключи web push не заданы в окружении сервера. Пока их нет, этот канал не работает
          ни у кого.
        </Provenance>
      ) : permission === 'unsupported' ? (
        <Provenance>
          Этот браузер не умеет показывать уведомления. Остаются панель и Telegram.
        </Provenance>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <p style={{ font: '400 14px/1.6 var(--font-text)', color: 'var(--text-muted)', maxWidth: 560 }}>
            Разрешение выдаётся на конкретное устройство и браузер. На другом устройстве его
            нужно выдать отдельно.
          </p>

          {devices.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              {devices.map((device) => (
                <div key={device.id}>
                  <div style={{ font: '400 13px var(--font-text)', color: 'var(--text-strong)' }}>
                    {device.userAgent ?? 'устройство без названия'}
                  </div>
                  <Provenance>
                    {`Разрешено ${formatDateTime(device.createdAt)}` +
                      (device.lastOkAt ? ` · последнее уведомление ${formatRelative(device.lastOkAt)}` : ' · уведомлений ещё не было')}
                  </Provenance>
                </div>
              ))}
            </div>
          ) : (
            <Provenance>Ни на одном устройстве разрешение пока не выдано.</Provenance>
          )}

          {error ? (
            <div style={{ font: '400 13px var(--font-text)', color: 'var(--status-danger-fg)' }}>
              {error}
            </div>
          ) : null}

          <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
            <Button
              variant="secondary"
              size={size}
              loading={subscribe.isPending}
              onClick={() => void enable()}
            >
              {linkedHere ? 'Обновить разрешение' : 'Разрешить на этом устройстве'}
            </Button>
            {thisDevice ? (
              <Button
                variant="ghost"
                size={size}
                loading={unsubscribe.isPending}
                onClick={() => void disable()}
              >
                Отключить здесь
              </Button>
            ) : null}
          </div>
        </div>
      )}
    </Card>
  );
}

export function NotificationsTab() {
  const settings = useNotificationSettings(true);
  const setType = useOperation<
    { type: string; isEnabled?: boolean; channels?: NotificationChannel[] },
    { type: string }
  >('notification.set_type');

  const types = settings.data?.types ?? [];
  const groups = ['money', 'tasks', 'system'] as const;

  if (settings.isLoading || settings.isError) {
    return <QueryState query={settings} what="настройки" />;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
      <p style={{ font: '400 13px/1.5 var(--font-text)', color: 'var(--text-muted)', maxWidth: 620 }}>
        Уведомление приходит, когда нужно действие. «Всё в порядке» не сообщается никогда.
        Настройки личные: у каждого свои каналы. С 23:00 до 08:00 в Telegram и браузер уходят
        только срочные, остальное копится до утренней сводки.
      </p>

      {groups.map((group) => {
        const inGroup = types.filter((type) => type.group === group);
        if (inGroup.length === 0) return null;
        const groupTitle = inGroup[0]?.groupTitle ?? group;

        return (
          <Card padding="md" key={group}>
            <div style={{ ...SECTION_TITLE, marginBottom: 'var(--space-5)' }}>{groupTitle}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
              {inGroup.map((setting) => (
                <TypeRow
                  key={setting.type}
                  setting={setting}
                  disabled={setType.isPending}
                  onChange={(input) => setType.mutate(input)}
                />
              ))}
            </div>
          </Card>
        );
      })}

      <TelegramCard />
      <PlacesCard />
      <TaskDigestCard />
      <WebPushCard />
    </div>
  );
}
